import type {PropertyProps} from 'grapesjs';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';

export interface ShortcutItem {
  label: string;
  keys: string;
}

export interface ShortcutListProps extends PropertyProps {
  class: string;
  list: ShortcutItem[];
}

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const styleManager = editor.StyleManager;

  const SHORTCUT_LIST_SEPARATOR = ', ';
  const SHORTCUT_SEPARATOR = '+';

  styleManager.addType(window.PMW.emails.styles.ID_SHORTCUT_LIST, {
    /**
     * Creates a shortcut list element
     */
    create(data) {
      const props = data.props as ShortcutListProps;
      const container = document.createElement('ul');
      container.className = props.type + ' shortcut-list-container ' + props.class;

      props.list.forEach((shortcut) => {
        const keys = getRelevantShortcutKeys(shortcut.keys);

        const li = document.createElement('li');
        const itemContainer = document.createElement('div');
        const shortcutContainer = document.createElement('div');
        const label = document.createElement('label');

        li.className = 'shortcut-list-item';
        itemContainer.className = 'flexbox flex-items-start';
        shortcutContainer.className = 'shortcut-container';
        label.className = 'shortcut-list-item-label body-md-regular';

        label.innerHTML = shortcut.label;

        keys.forEach((key, index) => {
          const charBox = document.createElement('span');

          charBox.className = 'body-md-bold ';
          charBox.className += key.length > 1 ? 'shortcut-string-box' : 'shortcut-char-box';
          charBox.innerHTML = toTitleCase(key);

          shortcutContainer.appendChild(charBox);

          if (index < keys.length - 1) {
            const plusSign = document.createElement('i');
            plusSign.className = 'icon size-icon-16 icon-plus';

            shortcutContainer.appendChild(plusSign);
          }
        });

        itemContainer.appendChild(label);
        itemContainer.appendChild(shortcutContainer);
        li.appendChild(itemContainer);
        container.appendChild(li);
      });

      return container;
    }
  });

  /**
   * Gets the keystrokes in an array relevant to the current OS
   */
  function getRelevantShortcutKeys(shortcuts: string): string[] {
    const isMac = isMacOS();
    const shortcutsList = shortcuts.split(SHORTCUT_LIST_SEPARATOR);
    const noAlternateShortcut = shortcutsList.length === 1;

    if (shortcutsList.length === 0) {
      return [];
    }

    if (noAlternateShortcut) {
      const singleKey = shortcutsList[0].length === 1;

      if (singleKey) {
        return shortcutsList;
      }

      return shortcutsList[0].split(SHORTCUT_SEPARATOR);
    }

    return filterShortcut(shortcutsList, isMac);
  }

  /**
   * Checks if the current OS is Mac
   */
  function isMacOS(): boolean {
    return navigator.userAgent.includes('Mac');
  }

  /**
   * Filters the given array of shortcuts based on the operating system
   */
  function filterShortcut(shortcutArray: string[], isMac: boolean): string[] {
    const isMacShortcut = (shortcut: string): boolean => shortcut.includes('⌘') || shortcut.includes('delete');

    const filter = isMac ? isMacShortcut : (shortcut: string) => !isMacShortcut(shortcut);
    const shortcut = shortcutArray.find(filter);

    return shortcut?.split(SHORTCUT_SEPARATOR) ?? [];
  }

  /**
   * Converts a string to title case
   */
  function toTitleCase(str: string): string {
    return str.replace(
      /\w\S*/g,
      function (txt) {
        return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase();
      }
    );
  }
};